import type { LocalHorizontal } from "../coordinates.ts";
import type {
  BuildingVisualGenerationOptions,
  VilniusBuildingStyleFamily,
} from "./types.ts";

/**
 * World-local bounds of the checked-in 4 x 4 Old Town development AOI.
 *
 * Source: data/fixtures/source/m1-old-town-aoi.json, reprojected relative to
 * its documented local origin. This is deliberately not a general Vilnius
 * district classifier; later style zones remain authored data.
 */
export const OLD_TOWN_DEVELOPMENT_STYLE_BOUNDS = Object.freeze({
  minX: 0,
  minZ: 0,
  maxX: 1_024,
  maxZ: 1_024,
});

export function classifyVilniusBuildingStyle(
  globalCentroid: Readonly<LocalHorizontal>,
  options: Readonly<BuildingVisualGenerationOptions> = {},
): VilniusBuildingStyleFamily {
  assertFinitePoint(globalCentroid);
  if (options.styleFamilyOverride !== undefined) {
    return options.styleFamilyOverride;
  }
  if (isWithinOldTownDevelopmentAoi(globalCentroid)) {
    return "old_town";
  }
  throw new Error(
    "Building centroid is outside the authored Old Town style AOI; " +
      "supply an explicit styleFamilyOverride",
  );
}

export function isWithinOldTownDevelopmentAoi(
  globalCentroid: Readonly<LocalHorizontal>,
): boolean {
  return (
    globalCentroid.x >= OLD_TOWN_DEVELOPMENT_STYLE_BOUNDS.minX &&
    globalCentroid.x <= OLD_TOWN_DEVELOPMENT_STYLE_BOUNDS.maxX &&
    globalCentroid.z >= OLD_TOWN_DEVELOPMENT_STYLE_BOUNDS.minZ &&
    globalCentroid.z <= OLD_TOWN_DEVELOPMENT_STYLE_BOUNDS.maxZ
  );
}

function assertFinitePoint(point: Readonly<LocalHorizontal>): void {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.z)) {
    throw new Error("Building global centroid must be finite");
  }
}
